import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { createOrder, trackPackageAdd, trackEvent } from "@/lib/api";
import { formatPrice, lineEstimate, computeTotals } from "@/lib/site";

const OrderListContext = createContext(null);
const STORAGE_KEY = "ek_order_list_v1";

export function OrderListProvider({ children }) {
  const [items, setItems] = useState(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      return raw ? JSON.parse(raw) : [];
    } catch {
      return [];
    }
  });
  const [isOpen, setIsOpen] = useState(false);
  const [startCheckout, setStartCheckout] = useState(false);

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
    } catch {
      /* ignore */
    }
  }, [items]);

  const trackListCreatedOnce = useCallback(() => {
    try {
      if (!sessionStorage.getItem("ek_list_created_sent")) {
        sessionStorage.setItem("ek_list_created_sent", "1");
        trackEvent("list_created");
      }
    } catch {
      /* ignore */
    }
  }, []);

  const addItem = useCallback((product, quantity = "1 kg") => {
    trackListCreatedOnce();
    trackEvent("product_add", { key: product.slug, label: product.name });
    setItems((prev) => {
      const exists = prev.find((i) => i.slug === product.slug);
      if (exists) {
        return prev.map((i) =>
          i.slug === product.slug ? { ...i, quantity } : i
        );
      }
      return [
        ...prev,
        {
          slug: product.slug,
          name: product.name,
          image: product.image,
          price: product.price ?? null,
          unit: product.unit || "kg",
          kind: product.kind || "meat",
          quantity,
          note: "",
        },
      ];
    });
  }, []);

  const addPackage = useCallback((pkg) => {
    trackPackageAdd(pkg.slug);
    trackListCreatedOnce();
    trackEvent("package_add", { key: pkg.slug, label: pkg.name });
    (pkg.items || []).forEach((it) =>
      trackEvent("product_add", { key: it.slug, label: it.name })
    );
    setItems((prev) => {
      const next = [...prev];
      pkg.items.forEach((it) => {
        const idx = next.findIndex((i) => i.slug === it.slug);
        const entry = {
          slug: it.slug,
          name: it.name,
          image: pkg.image,
          price: it.price ?? null,
          unit: it.unit || "kg",
          quantity: it.quantity,
          note: `${pkg.name} paketinden`,
        };
        if (idx >= 0) next[idx] = { ...next[idx], ...entry };
        else next.push(entry);
      });
      return next;
    });
  }, [trackListCreatedOnce]);

  // Senaryo / Mangal Hesaplayıcı — birden çok ürünü tek seferde ekler.
  const addItems = useCallback((newItems) => {
    trackListCreatedOnce();
    (newItems || []).forEach((it) =>
      trackEvent("product_add", { key: it.slug, label: it.name })
    );
    setItems((prev) => {
      const next = [...prev];
      newItems.forEach((it) => {
        const idx = next.findIndex((i) => i.slug === it.slug);
        const entry = {
          slug: it.slug,
          name: it.name,
          image: it.image,
          price: it.price ?? null,
          unit: it.unit || "kg",
          quantity: it.quantity || "1 kg",
          note: it.note || "",
        };
        if (idx >= 0) next[idx] = { ...next[idx], ...entry };
        else next.push(entry);
      });
      return next;
    });
  }, [trackListCreatedOnce]);

  const removeItem = useCallback((slug) => {
    setItems((prev) => prev.filter((i) => i.slug !== slug));
  }, []);

  const updateQuantity = useCallback((slug, quantity) => {
    setItems((prev) =>
      prev.map((i) => (i.slug === slug ? { ...i, quantity } : i))
    );
  }, []);

  const updateNote = useCallback((slug, note) => {
    setItems((prev) =>
      prev.map((i) => (i.slug === slug ? { ...i, note } : i))
    );
  }, []);

  const clear = useCallback(() => setItems([]), []);

  const isInList = useCallback(
    (slug) => items.some((i) => i.slug === slug),
    [items]
  );

  const buildMessage = useCallback(
    (site, details = null, orderNo = "") => {
      const brand = site?.brand || "Ertan Kasap";
      let lineSum = 0;
      const lines = items
        .map((i) => {
          const est = lineEstimate(i);
          if (est) lineSum += est;
          const priceStr = est ? ` (≈ ${formatPrice(est)})` : "";
          return `• ${i.name}: ${i.quantity}${priceStr}${i.note ? ` — Not: ${i.note}` : ""}`;
        })
        .join("\n");

      if (!details) {
        const totalStr =
          lineSum > 0
            ? `\n\nTahmini toplam: ${formatPrice(Math.round(lineSum))} (kesimde tartılarak kesinleşir)`
            : "";
        return `Merhaba ${brand},\n\nSipariş listem aşağıdaki gibidir:\n\n${lines}${totalStr}\n\nTeslimat / hazırlık süreci hakkında bilgi almak istiyorum.`;
      }

      const t = computeTotals(lineSum, details.fulfillment);
      let totalStr = "";
      if (t.subtotal > 0) {
        if (t.discountAmount > 0) {
          totalStr =
            `\n\nAra toplam: ${formatPrice(t.subtotal)}` +
            `\nDükkândan Al indirimi (%${t.discountPct}): -${formatPrice(t.discountAmount)}` +
            `\nTahmini toplam: ${formatPrice(t.total)} (kesimde tartılarak kesinleşir)`;
        } else {
          totalStr = `\n\nTahmini toplam: ${formatPrice(t.total)} (kesimde tartılarak kesinleşir)`;
        }
      }

      const noStr = orderNo ? ` (${orderNo})` : "";
      const info = [];
      info.push(`Ad Soyad: ${details.customer_name}`);
      info.push(`Telefon: ${details.phone}`);
      if (details.fulfillment === "pickup") {
        info.push(`Teslimat: Dükkândan teslim alacağım (%${t.discountPct} indirim)`);
      } else {
        info.push(`Teslimat: Adrese teslim — ${details.zone}`);
        info.push(`Adres: ${details.address}`);
      }
      if (details.delivery_time) info.push(`Zaman: ${details.delivery_time}`);
      info.push(`Ödeme: ${details.payment === "card" ? "Kapıda Kredi Kartı" : "Kapıda Nakit"}`);
      if (details.order_note) info.push(`Sipariş notu: ${details.order_note}`);

      return `Merhaba ${brand},\n\nYeni siparişim${noStr}:\n\n${lines}${totalStr}\n\n— — —\n${info.join("\n")}`;
    },
    [items]
  );

  const total = items.reduce((s, i) => s + (lineEstimate(i) || 0), 0);

  const orderItemsPayload = useCallback(
    () =>
      items.map((i) => ({
        slug: i.slug,
        name: i.name,
        quantity: i.quantity,
        price: i.price ?? null,
        unit: i.unit || "kg",
        note: i.note || "",
        line_estimate: lineEstimate(i) ?? null,
      })),
    [items]
  );

  // Mobil alt çubuk / hızlı gönder → paneli doğrudan checkout adımına açar
  // (kontrollü akış: ad/adres/ödeme adımları atlanmaz).
  const openForCheckout = useCallback(() => {
    if (!items.length) return;
    setStartCheckout(true);
    setIsOpen(true);
  }, [items.length]);

  // Kontrollü sipariş: müşteri bilgilerini kaydeder, Sipariş No alır, WhatsApp'a düzenli mesaj gönderir.
  const submitOrder = useCallback(
    async (site, details) => {
      if (!items.length || !site?.whatsapp) return { ok: false };
      const t = computeTotals(total, details.fulfillment);
      // popup engellenmesin diye sekmeyi kullanıcı hareketi içinde önce aç
      const waTab = window.open("", "_blank");
      let orderNo = "";
      try {
        const res = await createOrder({
          items: orderItemsPayload(),
          message: buildMessage(site, details),
          total_amount: t.total,
          subtotal: t.subtotal,
          discount_percent: t.discountPct,
          discount_amount: t.discountAmount,
          customer_name: details.customer_name,
          phone: details.phone,
          fulfillment: details.fulfillment,
          zone: details.zone,
          address: details.address,
          delivery_time: details.delivery_time,
          payment: details.payment,
          order_note: details.order_note,
        });
        orderNo = res?.order_no || "";
      } catch {
        /* kayıt başarısız olsa da WhatsApp'a yönlendir */
      }
      trackEvent("whatsapp_send", { amount: t.total, label: `${items.length} kalem` });
      const msg = buildMessage(site, details, orderNo);
      const url = `https://wa.me/${site.whatsapp}?text=${encodeURIComponent(msg)}`;
      if (waTab) waTab.location.href = url;
      else window.open(url, "_blank", "noopener,noreferrer");
      return { ok: true, orderNo };
    },
    [items, buildMessage, total, orderItemsPayload]
  );

  const value = {
    items,
    count: items.length,
    total,
    isOpen,
    setIsOpen,
    startCheckout,
    setStartCheckout,
    openForCheckout,
    addItem,
    addPackage,
    addItems,
    removeItem,
    updateQuantity,
    updateNote,
    clear,
    isInList,
    buildMessage,
    submitOrder,
  };

  return (
    <OrderListContext.Provider value={value}>
      {children}
    </OrderListContext.Provider>
  );
}

export function useOrderList() {
  const ctx = useContext(OrderListContext);
  if (!ctx) throw new Error("useOrderList must be used within OrderListProvider");
  return ctx;
}
